import { getAllStudyTemplatesForInstitution } from 'complionAdmin/actions/delegationOfAuthorityConfiguration';

import React, { useEffect, useRef, useState } from 'react';

import { Conditional } from 'common/components/layout';
import { binderInstitutionIdSelector } from 'common/selectors';
import lodash, { isEmpty } from 'lodash';
import uuid from 'node-uuid';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { Box, ClickAwayListener, Drawer, TextField, Typography } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

import { addApplicationMessage } from 'common/actions/applicationMessagesActions';

import { Button, BUTTON } from 'components/common/Button/Button';

import { saveStudyTaskV2 } from 'pages/delegationOfAuthority/v2/actions/studyTasksV2';

import DraggableTaskList from './DraggableTaskList';
import ConfirmRemovalPanel from './ConfirmRemovalPanel';
import styles from './SetupTask.scss';
import { loadHomePageV4Access } from '../../../../../../common/features/predicates';

const { COLOR, BGCOLOR } = BUTTON;

const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
};
function SetupTask(props) {
    const taskList = lodash.get(props, 'doaStudyTasks', []);

    const [items, setItems] = useState([]);
    const [taskName, setTaskName] = useState('');

    const prevList = useRef(items);
    const [isNewTask, setNewTask] = useState(false);
    const [deletedTasks, setDeletedTasks] = useState([]);
    const [isOpenRemovalPanel, setOpenRemovalPanel] = useState(false);

    // Keep old list to compare with new list => Auto scroll to bottom when add new item in list
    useEffect(() => {
        prevList.current = items;
    }, [items]);

    // Revert data when open/close panel
    useEffect(() => {
        setItems(taskList);

        setDeletedTasks([]);
        setOpenRemovalPanel(false);
    }, [props.open]);

    useEffect(() => {
        if(!props.open) return;
        const institutionId = props.institutionId;
        props.getAllStudyTemplatesForInstitution(institutionId);
    }, [props.open]);

    const onDragEnd = ({ destination, source }) => {
        // Dropped outside the list
        if (!destination) return;
        const newItems = reorder(items, source.index, destination.index);
        newItems.forEach((item, index) => (item.taskIndex = index + 1));

        setItems(newItems);
    };

    const onEnterAddTask = (e) => {
        if (e.key === 'Enter') {
            handleAddTask();
        }
    };

    const handleAddTask = () => {
        // Prepare data for update list
        if (taskName.trim() === '') return;

        const newTaskList = [...items];
        // Return when taskName exactly same with which in doaStudyTasks
        const existedTask = lodash.find(deletedTasks, { taskName });
        const isTaskTheSameWithDefault = !isEmpty(existedTask) && lodash.some(taskList, { id: existedTask.id });

        if (isTaskTheSameWithDefault) {
            setDeletedTasks(lodash.filter(deletedTasks, (task) => task.id !== existedTask.id));
            newTaskList.push({ ...existedTask, taskIndex: items.length + 1 });
        } else {
            newTaskList.push({
                id: uuid.v4(),
                taskIndex: items.length + 1,
                taskName,
            });
        }

        setItems(newTaskList);
        setTaskName('');
    };

    const handleSaveChange = () => {
        if (!isEmpty(deletedTasks)) {
            setOpenRemovalPanel(true);
            return;
        }
        const studyId = lodash.get(props, ['study', 'id']);

        props.saveStudyTaskV2(items, props.binder.id, studyId);
        props.onClose();
    };

    const handleImportTemplateStudy = () => {
        if (isEmpty(props.template)) {
            props.addApplicationMessage('error', 'Failed to import study key template');
            return;
        }
        const template = props.template[0].taskNames;

        const taskListImport = template.map((item, index) => ({
            id: uuid.v4(),
            taskIndex: index + 1,
            taskName: item,
        }));

        setItems(taskListImport);
    };

    const handleCloseSidePanel = () => {
        props.onClose();
        setNewTask(false);
        setTaskName('');
    };

    const handleClickAwayInput = () => {
        if (!isNewTask) return;

        setNewTask(false);
        setTaskName('');
    };

    const handleDeleteTask = (removedItem) => {
        const updatedItems = lodash
            .chain(items)
            .filter((item) => item.id !== removedItem.id)
            .map((item) => ({
                id: item.id,
                taskName: item.taskName,
                taskIndex: item.taskIndex > removedItem.taskIndex ? item.taskIndex - 1 : item.taskIndex,
            }))
            .value();
        setItems(updatedItems);

        const isTaskTheSameWithDefault = lodash.some(taskList, { id: removedItem.id });
        if (isTaskTheSameWithDefault) {
            setDeletedTasks([...deletedTasks, removedItem]);
        }
    };

    const renderStudyTaskPanel = () => {
        return (
            <Box className={styles.Container}>
                <Box className={styles.Container__Header}>
                    <Box className={styles.Header}>
                        <Typography variant="h6" color="primary">
                            Setup {props.complion4FlagEnabled? 'Binder' : 'Study'} Tasks
                        </Typography>
                        <CloseIcon
                            className={styles.Header__CloseIcon}
                            onClick={handleCloseSidePanel}
                        />
                    </Box>

                    <Box marginBottom={3}>
                        <Typography variant="body2">
                            Define the tasks performed for this {props.complion4FlagEnabled? 'binder' : 'study'}. You can start with your
                            institution’s task template and modify it as needed, or create a task
                            list from scratch.
                        </Typography>
                    </Box>

                    <Box className={styles.BlockAction}>
                        <Conditional condition={isNewTask}>
                            <ClickAwayListener onClickAway={handleClickAwayInput}>
                                <Box className={styles.BlockInput}>
                                    <TextField
                                        placeholder="Type task title here"
                                        variant="outlined"
                                        fullWidth={true}
                                        InputProps={{
                                            className: taskName ? styles.BlockInput__input : null,
                                        }}
                                        value={taskName}
                                        onChange={(e) => {
                                            setTaskName(e.target.value);
                                        }}
                                        onKeyDown={onEnterAddTask}
                                    />
                                    <Conditional condition={taskName.length > 0}>
                                        <Box className={styles.BlockInput__wrapper}>
                                            <Typography className={styles.BlockInput__label}>
                                                Use ´Enter´ or
                                            </Typography>
                                            <Button
                                                data-testid="SetupTask__Btn__Enter"
                                                className={styles.BlockInput__button}
                                                text={'Add Task'}
                                                color={COLOR.WHITE}
                                                bgcolor={BGCOLOR.BLUE}
                                                onClick={handleAddTask}
                                            />
                                        </Box>
                                    </Conditional>
                                </Box>
                            </ClickAwayListener>
                        </Conditional>
                        <Conditional condition={!isNewTask}>
                            <Box className={styles.BlockAddTask}>
                                <Button
                                    text={props.complion4FlagEnabled? 'Add Binder Tasks' : 'Add Study Tasks'}
                                    data-testid="SetupTask__Btn__AddTask"
                                    color={COLOR.WHITE}
                                    bgcolor={BGCOLOR.BLUE}
                                    onClick={() => {
                                        setNewTask(true);
                                    }}
                                />
                                <Conditional condition={items.length <= 0}>
                                    <Button
                                        text={'Import Templated Tasks'}
                                        outline={BUTTON.OUTCOLOR.MEDIUMGREY}
                                        color={COLOR.BLACK}
                                        bgcolor={BGCOLOR.WHITE}
                                        onClick={handleImportTemplateStudy}
                                        data-testid="SetupTask__Btn__Import"
                                    />
                                </Conditional>
                            </Box>
                        </Conditional>
                    </Box>

                    <Box className={styles.BlockInfo}>
                        <Typography variant="body2">
                            Numbers will be appended automatically to your task list when the
                            delegation data is exported as a PDF.
                        </Typography>
                    </Box>

                    <Typography variant="h6">
                        {items.length} {props.complion4FlagEnabled? 'Binder' : 'Study'} Task{items.length !== 1 && 's'}
                    </Typography>
                </Box>

                <Box className={styles.Container__List}>
                    <DraggableTaskList
                        items={items}
                        lengthPrevItems={prevList.current.length}
                        onDragEnd={onDragEnd}
                        onDelete={handleDeleteTask}
                    />
                </Box>

                <Box className={styles.Container__Footer}>
                    <Button
                        text={'Cancel'}
                        outline={BUTTON.OUTCOLOR.MEDIUMGREY}
                        color={COLOR.BLACK}
                        bgcolor={BGCOLOR.WHITE}
                        onClick={handleCloseSidePanel}
                        data-testid="SetupTask__Btn__Cancel"
                    />
                    <Button
                        text={'Save Changes'}
                        bgcolor={BGCOLOR.ORANGE}
                        color={COLOR.WHITE}
                        onClick={handleSaveChange}
                        disabled={lodash.isEqual(items, taskList)}
                        data-testid="SetupTask__Btn__SaveChange"
                    />
                </Box>
            </Box>
        );
    };

    return (
        <Drawer className={styles.Modal} variant="temporary" anchor="right" open={props.open}>
            <Conditional condition={!isOpenRemovalPanel}>
                {renderStudyTaskPanel()}
            </Conditional>
            <Conditional condition={isOpenRemovalPanel}>
                <ConfirmRemovalPanel
                    isOpened={props.open}
                    currentTaskList={items}
                    removedTaskList={deletedTasks}
                    onClose={props.onClose}
                />
            </Conditional>
        </Drawer>
    );
}

SetupTask.propTypes = {
    onClose: PropTypes.func,
    open: PropTypes.bool.isRequired,
};

export default connect(
    (state, _props) => {
        const binder = lodash.get(state, 'binder', {});
        const doaStudyTasks = lodash.get(state, 'doaStudyTasks', []);
        const institutionId = binderInstitutionIdSelector(state);
        const template = lodash.get(state, 'studyTemplates', []);
        const study = lodash.get(state, 'study', {});

        return {
            study,
            binder,
            doaStudyTasks,
            institutionId,
            template,
            complion4FlagEnabled: loadHomePageV4Access(state),
        };
    },
    {
        saveStudyTaskV2,
        getAllStudyTemplatesForInstitution,
        addApplicationMessage,
    }
)(SetupTask);
